import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from './api.js';

/**
 * BRANDING MECHANISM
 * ------------------
 * No component imports a logo file or hardcodes a colour. Everything reads from
 * this context, which is populated at runtime from /api/branding, and the
 * accent colour is pushed into a CSS custom property so plain CSS can use it
 * too. Swapping the studio identity later is a form submission in the admin -
 * no code change, no rebuild, no redeploy.
 */

const BrandingContext = createContext({ branding: null, reload: () => {} });

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(null);

  const reload = useCallback(async () => {
    try {
      setBranding(await api.get('/api/branding'));
    } catch {
      setBranding({ studio_name: 'Studio', accent_color: '#8a7358' });
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    if (!branding?.accent_color) return;
    document.documentElement.style.setProperty('--accent', branding.accent_color);
    document.documentElement.style.setProperty('--accent-soft', withAlpha(branding.accent_color, 0.12));
    if (branding.studio_name) document.title = branding.studio_name;
  }, [branding]);

  return <BrandingContext.Provider value={{ branding, reload }}>{children}</BrandingContext.Provider>;
}

export const useBranding = () => useContext(BrandingContext);

function withAlpha(hex, alpha) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!m) return hex;
  const [r, g, b] = m.slice(1).map((h) => parseInt(h, 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function StudioMark({ className = '' }) {
  const { branding } = useBranding();
  if (!branding) return null;
  return branding.logo_url ? (
    <img src={branding.logo_url} alt={branding.studio_name} className={`studio-logo ${className}`} />
  ) : (
    <span className={`studio-wordmark ${className}`}>{branding.studio_name}</span>
  );
}
